import { useState, useEffect } from 'react'
import { X, AlertCircle, Package, DollarSign, Camera, Upload, Trash2, Image as ImageIcon } from 'lucide-react'

interface Sale {
  id: string
  order_number?: string
  sale_invoice?: string
  sale_date?: string
  customer_name?: string
  customer_email?: string
  customer_phone?: string
  amount?: number
  profit?: number
}

interface Item {
  id: string
  name: string
  sku?: string
}

interface ReturnItemModalProps {
  isOpen: boolean
  onClose: () => void
  item: Item
  sale?: Sale | null
  existingReturn?: any // For edit mode
  onSuccess: (returnData: any) => void
}

const RETURN_REASONS = [
  { code: 'DEF', label: 'Defective' },
  { code: 'WRO', label: 'Wrong Item' },
  { code: 'SIZ', label: 'Size Issue' },
  { code: 'CHA', label: 'Changed Mind' },
  { code: 'DAM', label: 'Damaged' },
  { code: 'NOT', label: 'Not as Described' },
  { code: 'OTH', label: 'Other' }
]

const CONDITIONS = [
  { code: 'NEW', label: 'New - Unopened' },
  { code: 'GEN', label: 'Gently Used' },
  { code: 'DAM', label: 'Damaged' },
  { code: 'DEF', label: 'Defective' }
]

const PAYMENT_TYPES = [
  'marketplace',
  'credit_card',
  'cash',
  'store_credit',
  'check',
  'paypal',
  'venmo',
  'other'
]

export default function ReturnItemModal({
  isOpen,
  onClose,
  item,
  sale,
  existingReturn,
  onSuccess
}: ReturnItemModalProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [generatedRMA, setGeneratedRMA] = useState<string | null>(null)
  const [uploadedImages, setUploadedImages] = useState<Array<{url: string, type: string}>>([])
  const [uploadingImages, setUploadingImages] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    status: 'pending',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    order_number: '',
    return_reason: 'SIZ',
    reason_details: '',
    condition: 'GEN',
    return_method: 'C',
    priority: 'STA',
    payment_type: 'marketplace',
    original_sale_amount: '',
    original_profit: '',
    refund_amount: '',
    restocking_fee: '0',
    return_shipping_cost: '0',
    restocking_cost: '0',
    resell_value: '',
    tracking_number: '',
    carrier: '',
    expected_arrival_date: '',
    internal_notes: '',
    customer_notes: ''
  })

  // Populate form from sale or existing return
  useEffect(() => {
    if (existingReturn) {
      // Edit mode - populate from existing return
      setFormData({
        status: existingReturn.status || 'pending',
        customer_name: existingReturn.customer_name || '',
        customer_email: existingReturn.customer_email || '',
        customer_phone: existingReturn.customer_phone || '',
        order_number: existingReturn.order_number || '',
        return_reason: existingReturn.return_reason || 'SIZ',
        reason_details: existingReturn.reason_details || '',
        condition: existingReturn.condition || 'GEN',
        return_method: existingReturn.return_method || 'C',
        priority: existingReturn.priority || 'STA',
        payment_type: existingReturn.payment_type || 'marketplace',
        original_sale_amount: existingReturn.original_sale_amount?.toString() || '',
        original_profit: existingReturn.original_profit?.toString() || '',
        refund_amount: existingReturn.refund_amount?.toString() || '',
        restocking_fee: existingReturn.restocking_fee?.toString() || '0',
        return_shipping_cost: existingReturn.return_shipping_cost?.toString() || '0',
        restocking_cost: existingReturn.restocking_cost?.toString() || '0',
        resell_value: existingReturn.resell_value?.toString() || '',
        tracking_number: existingReturn.tracking_number || '',
        carrier: existingReturn.carrier || '',
        expected_arrival_date: existingReturn.expected_arrival_date || '',
        internal_notes: existingReturn.internal_notes || '',
        customer_notes: existingReturn.customer_notes || ''
      })
      setGeneratedRMA(existingReturn.rma_number)

      // Populate existing images if available
      if (existingReturn.images && existingReturn.images.length > 0) {
        const existingImages = existingReturn.images.map((img: any) => ({
          url: img.image_url,
          type: img.image_type || 'other'
        }))
        setUploadedImages(existingImages)
      }
    } else if (sale) {
      // New return from sale - auto-populate
      setFormData(prev => ({
        ...prev,
        customer_name: sale.customer_name || '',
        customer_email: sale.customer_email || '',
        customer_phone: sale.customer_phone || '',
        order_number: sale.order_number || '',
        original_sale_amount: sale.amount?.toString() || '',
        original_profit: sale.profit?.toString() || ''
      }))
    }
  }, [sale, existingReturn])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  // Image upload handlers
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      setUploadingImages(true)
      try {
        const imagePromises = Array.from(files).map(async (file) => {
          return new Promise<string>((resolve) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result as string)
            reader.readAsDataURL(file)
          })
        })

        const base64Images = await Promise.all(imagePromises)

        // Add images with type classification
        const newImages = base64Images.map((url, index) => ({
          url,
          type: 'condition' // Default type, can be changed later
        }))

        setUploadedImages(prev => [...prev, ...newImages])
      } catch (error) {
        console.error('Error processing images:', error)
        setError('Failed to process images')
      } finally {
        setUploadingImages(false)
      }
    }
  }

  const handleCameraCapture = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.capture = 'environment'
    input.multiple = true
    input.onchange = handleImageUpload as any
    input.click()
  }

  const handleFileUpload = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.multiple = true
    input.onchange = handleImageUpload as any
    input.click()
  }

  const removeImage = (index: number) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index))
  }

  const changeImageType = (index: number, type: string) => {
    setUploadedImages(prev => prev.map((img, i) =>
      i === index ? { ...img, type } : img
    ))
  }

  const generateRMA = () => {
    const today = new Date()
    const dateStr = String(today.getMonth() + 1).padStart(2, '0') +
                   String(today.getDate()).padStart(2, '0') +
                   String(today.getFullYear())

    const increment = '001' // This will come from backend

    const rma = `BGR-R-${formData.return_reason}-${dateStr}-${increment}-${formData.condition}-${formData.return_method}-${formData.priority}`

    setGeneratedRMA(rma)
  }

  const calculateFinancials = () => {
    const refund = parseFloat(formData.refund_amount) || 0
    const shipping = parseFloat(formData.return_shipping_cost) || 0
    const restockingCost = parseFloat(formData.restocking_cost) || 0
    const restockingFee = parseFloat(formData.restocking_fee) || 0
    const resellValue = parseFloat(formData.resell_value) || 0

    const netImpact = -(refund + shipping + restockingCost) + restockingFee
    const potentialRevenue = resellValue > 0 ? (resellValue * 0.75) - shipping : 0

    return {
      netImpact: netImpact.toFixed(2),
      potentialRevenue: potentialRevenue.toFixed(2)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      // Validation - customer info is now optional
      // No required fields for customer info

      const payload = {
        ...formData,
        sales_id: sale?.id || null,
        sale_invoice: sale?.sale_invoice || null,
        sale_date: sale?.sale_date || null,
        original_sale_amount: formData.original_sale_amount ? parseFloat(formData.original_sale_amount) : null,
        original_profit: formData.original_profit ? parseFloat(formData.original_profit) : null,
        refund_amount: formData.refund_amount ? parseFloat(formData.refund_amount) : null,
        restocking_fee: parseFloat(formData.restocking_fee),
        return_shipping_cost: parseFloat(formData.return_shipping_cost),
        restocking_cost: parseFloat(formData.restocking_cost),
        resell_value: formData.resell_value ? parseFloat(formData.resell_value) : 0,
        items: [{
          inventory_item_id: item.id,
          item_name: item.name,
          item_sku: item.sku || null,
          quantity: 1
        }]
      }

      console.log('Creating return with payload:', payload)
      console.log('Item ID:', item.id)

      const url = existingReturn
        ? `/api/returns-pg/${existingReturn.id}`
        : '/api/returns-pg'

      const method = existingReturn ? 'PUT' : 'POST'

      console.log('Fetching:', url, method)

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      console.log('Response status:', response.status)

      const result = await response.json()

      console.log('Response result:', result)

      if (!result.success) {
        console.error('API error:', result.error)
        throw new Error(result.error || 'Failed to save return')
      }

      console.log('Return created successfully:', result.data)

      // Set the generated RMA for display
      if (result.data.rma_number) {
        setGeneratedRMA(result.data.rma_number)
      }

      // Upload images if any exist
      if (uploadedImages.length > 0) {
        try {
          for (const image of uploadedImages) {
            // Convert base64 to blob
            const base64Data = image.url.split(',')[1]
            const byteCharacters = atob(base64Data)
            const byteNumbers = new Array(byteCharacters.length)
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i)
            }
            const byteArray = new Uint8Array(byteNumbers)
            const blob = new Blob([byteArray], { type: 'image/jpeg' })

            // Create FormData with file
            const formData = new FormData()
            formData.append('file', blob, `return_${result.data.rma_number}_${Date.now()}.jpg`)

            // Upload to media-assets
            const mediaResponse = await fetch('/api/media-assets', {
              method: 'POST',
              body: formData
            })

            const mediaResult = await mediaResponse.json()

            if (mediaResult.success && mediaResult.data) {
              // Link image to return
              await fetch('/api/return-images-pg', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  return_id: result.data.id,
                  media_id: mediaResult.data.id,
                  image_type: image.type
                })
              })
            }
          }
        } catch (imgErr) {
          console.error('Failed to upload some images:', imgErr)
          // Don't fail the whole operation if image upload fails
        }
      }

      onSuccess(result.data)
      onClose()

    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  const financials = calculateFinancials()
  const isEditMode = !!existingReturn

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '24px'
    }}>
      <div style={{
        background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
        border: '2px solid #9D4EDD',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '900px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 0 40px rgba(157, 78, 221, 0.4)',
        fontFamily: 'Rajdhani, sans-serif'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '2px solid #9D4EDD',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(90deg, rgba(157, 78, 221, 0.2), transparent)'
        }}>
          <div>
            <h2 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '32px',
              fontWeight: 700,
              color: '#9D4EDD',
              margin: 0,
              textTransform: 'uppercase',
              letterSpacing: '2px',
              textShadow: '0 0 20px rgba(157, 78, 221, 0.6)'
            }}>
              {isEditMode ? 'EDIT RETURN' : (sale ? 'NEW RETURN // FROM SALE' : 'NEW RETURN // MANUAL ENTRY')}
            </h2>
            <p style={{
              color: '#8A95AA',
              fontSize: '14px',
              margin: '4px 0 0 0'
            }}>
              {item.name} {item.sku && `(${item.sku})`}
            </p>
            {isEditMode && (
              <div style={{
                marginTop: '8px',
                padding: '8px 12px',
                background: 'rgba(255, 107, 53, 0.2)',
                border: '1px solid #FF6B35',
                borderRadius: '4px',
                color: '#FF6B35',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'uppercase'
              }}>
                EDIT MODE: Long-Running Process
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '6px',
              padding: '8px',
              cursor: 'pointer',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X style={{ width: 20, height: 20 }} />
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div style={{
            margin: '16px 24px',
            padding: '12px',
            background: 'rgba(255, 0, 110, 0.2)',
            border: '1px solid #FF006E',
            borderRadius: '6px',
            color: '#FF006E',
            display: 'flex',
            gap: '8px',
            alignItems: 'center'
          }}>
            <AlertCircle style={{ width: 20, height: 20, flexShrink: 0 }} />
            <span>{error}</span>
          </div>
        )}

        {/* RMA Display */}
        {generatedRMA && (
          <div style={{
            margin: '16px 24px',
            padding: '16px',
            background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.1), rgba(0, 184, 212, 0.1))',
            border: '2px solid #00F0FF',
            borderRadius: '8px',
            boxShadow: '0 0 20px rgba(0, 240, 255, 0.3)'
          }}>
            <div style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '24px',
              fontWeight: 700,
              color: '#00F0FF',
              letterSpacing: '2px',
              textAlign: 'center',
              textShadow: '0 0 10px rgba(0, 240, 255, 0.6)'
            }}>
              RMA: {generatedRMA}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Form Content */}
          <div style={{ padding: '24px' }}>
            {/* Customer Information */}
            <div style={{
              marginBottom: '24px',
              padding: '16px',
              background: !sale ? 'rgba(255, 107, 53, 0.1)' : 'rgba(0, 0, 0, 0.2)',
              border: !sale ? '2px dashed #FF6B35' : '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px'
            }}>
              <h3 style={{
                color: '#9D4EDD',
                fontSize: '18px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '16px'
              }}>
                Customer Information {!sale && <span style={{ color: '#FF6B35' }}>(Manual Entry)</span>}
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                    CUSTOMER NAME
                  </label>
                  <input
                    type="text"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleChange}
                    placeholder="Optional"
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'Rajdhani, sans-serif'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                    ORDER NUMBER
                  </label>
                  <input
                    type="text"
                    name="order_number"
                    value={formData.order_number}
                    onChange={handleChange}
                    placeholder="Optional"
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'Rajdhani, sans-serif'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                    EMAIL
                  </label>
                  <input
                    type="email"
                    name="customer_email"
                    value={formData.customer_email}
                    onChange={handleChange}
                    placeholder="Optional"
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'Rajdhani, sans-serif'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                    PHONE
                  </label>
                  <input
                    type="tel"
                    name="customer_phone"
                    value={formData.customer_phone}
                    onChange={handleChange}
                    placeholder="Optional"
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'Rajdhani, sans-serif'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Return Details */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                color: '#9D4EDD',
                fontSize: '18px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '16px'
              }}>
                Return Details
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                    RETURN REASON <span style={{ color: '#FF006E' }}>*</span>
                  </label>
                  <select
                    name="return_reason"
                    value={formData.return_reason}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'Rajdhani, sans-serif'
                    }}
                  >
                    {RETURN_REASONS.map(reason => (
                      <option key={reason.code} value={reason.code} style={{ background: '#1A1F2E' }}>
                        {reason.code} - {reason.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                    CONDITION <span style={{ color: '#FF006E' }}>*</span>
                  </label>
                  <select
                    name="condition"
                    value={formData.condition}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'Rajdhani, sans-serif'
                    }}
                  >
                    {CONDITIONS.map(cond => (
                      <option key={cond.code} value={cond.code} style={{ background: '#1A1F2E' }}>
                        {cond.code} - {cond.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                    RETURN SHIPPING <span style={{ color: '#FF006E' }}>*</span>
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', background: formData.return_method === 'C' ? 'rgba(0, 240, 255, 0.2)' : 'rgba(0, 0, 0, 0.3)', border: formData.return_method === 'C' ? '2px solid #00F0FF' : '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '4px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="return_method"
                        value="C"
                        checked={formData.return_method === 'C'}
                        onChange={handleChange}
                      />
                      <span style={{ color: 'white', fontSize: '14px' }}>Customer Paid</span>
                    </label>
                    <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', background: formData.return_method === 'B' ? 'rgba(0, 240, 255, 0.2)' : 'rgba(0, 0, 0, 0.3)', border: formData.return_method === 'B' ? '2px solid #00F0FF' : '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '4px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="return_method"
                        value="B"
                        checked={formData.return_method === 'B'}
                        onChange={handleChange}
                      />
                      <span style={{ color: 'white', fontSize: '14px' }}>BGS Paid</span>
                    </label>
                  </div>
                </div>

                {existingReturn && (
                  <div>
                    <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                      STATUS <span style={{ color: '#FF006E' }}>*</span>
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: 'rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '4px',
                        color: 'white',
                        fontSize: '14px',
                        fontFamily: 'Rajdhani, sans-serif'
                      }}
                    >
                      <option value="pending" style={{ background: '#1A1F2E' }}>Pending</option>
                      <option value="approved" style={{ background: '#1A1F2E' }}>Approved</option>
                      <option value="label_sent" style={{ background: '#1A1F2E' }}>Label Sent</option>
                      <option value="in_transit" style={{ background: '#1A1F2E' }}>In Transit</option>
                      <option value="received" style={{ background: '#1A1F2E' }}>Received</option>
                      <option value="inspecting" style={{ background: '#1A1F2E' }}>Inspecting</option>
                      <option value="accepted" style={{ background: '#1A1F2E' }}>Accepted</option>
                      <option value="rejected" style={{ background: '#1A1F2E' }}>Rejected</option>
                      <option value="restocking" style={{ background: '#1A1F2E' }}>Restocking</option>
                      <option value="refund_processing" style={{ background: '#1A1F2E' }}>Refund Processing</option>
                      <option value="completed" style={{ background: '#1A1F2E' }}>Completed</option>
                      <option value="cancelled" style={{ background: '#1A1F2E' }}>Cancelled</option>
                    </select>
                  </div>
                )}

                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                    PRIORITY <span style={{ color: '#FF006E' }}>*</span>
                  </label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'Rajdhani, sans-serif'
                    }}
                  >
                    <option value="STA" style={{ background: '#1A1F2E' }}>STA - Standard</option>
                    <option value="EXP" style={{ background: '#1A1F2E' }}>EXP - Expedited</option>
                    <option value="VIP" style={{ background: '#1A1F2E' }}>VIP - VIP Customer</option>
                  </select>
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                    REASON DETAILS <span style={{ color: '#FF006E' }}>*</span>
                  </label>
                  <textarea
                    name="reason_details"
                    value={formData.reason_details}
                    onChange={handleChange}
                    required
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'Rajdhani, sans-serif',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Financial Information */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                color: '#9D4EDD',
                fontSize: '18px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '16px'
              }}>
                Financial Information
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                    PAYMENT TYPE <span style={{ color: '#FF006E' }}>*</span>
                  </label>
                  <select
                    name="payment_type"
                    value={formData.payment_type}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'Rajdhani, sans-serif'
                    }}
                  >
                    {PAYMENT_TYPES.map(type => (
                      <option key={type} value={type} style={{ background: '#1A1F2E' }}>
                        {type.replace('_', ' ').toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                    REFUND AMOUNT
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="refund_amount"
                    value={formData.refund_amount}
                    onChange={handleChange}
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'Rajdhani, sans-serif'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                    RETURN SHIPPING COST <span style={{ color: '#FF006E' }}>*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="return_shipping_cost"
                    value={formData.return_shipping_cost}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'Rajdhani, sans-serif'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                    RESTOCKING COST <span style={{ color: '#FF006E' }}>*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="restocking_cost"
                    value={formData.restocking_cost}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'Rajdhani, sans-serif'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                    RESTOCKING FEE <span style={{ color: '#FF006E' }}>*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="restocking_fee"
                    value={formData.restocking_fee}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'Rajdhani, sans-serif'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                    RESELL VALUE <span style={{ color: '#FF006E' }}>*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="resell_value"
                    value={formData.resell_value}
                    onChange={handleChange}
                    required
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'Rajdhani, sans-serif'
                    }}
                  />
                </div>
              </div>

              {/* Financial Preview */}
              <div style={{
                marginTop: '16px',
                padding: '16px',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <div style={{ color: '#8A95AA', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                      NET IMPACT (ACTUAL LOSS)
                    </div>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: 700,
                      color: parseFloat(financials.netImpact) < 0 ? '#FF006E' : '#00FF41'
                    }}>
                      ${financials.netImpact}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#8A95AA', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                      POTENTIAL FUTURE REVENUE
                    </div>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: 700,
                      color: '#00F0FF'
                    }}>
                      ${financials.potentialRevenue}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tracking Information */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                color: '#9D4EDD',
                fontSize: '18px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '16px'
              }}>
                Shipping & Tracking
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                    TRACKING NUMBER
                  </label>
                  <input
                    type="text"
                    name="tracking_number"
                    value={formData.tracking_number}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'Rajdhani, sans-serif'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                    CARRIER
                  </label>
                  <input
                    type="text"
                    name="carrier"
                    value={formData.carrier}
                    onChange={handleChange}
                    placeholder="e.g., USPS, UPS, FedEx"
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'Rajdhani, sans-serif'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                    EXPECTED ARRIVAL DATE
                  </label>
                  <input
                    type="date"
                    name="expected_arrival_date"
                    value={formData.expected_arrival_date}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'Rajdhani, sans-serif'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Images Section */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                color: '#9D4EDD',
                fontSize: '18px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '16px'
              }}>
                Documentation Images
              </h3>

              {/* Upload Buttons */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <button
                  type="button"
                  onClick={handleCameraCapture}
                  disabled={uploadingImages}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'linear-gradient(135deg, #00F0FF, #00B8D4)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '14px',
                    cursor: uploadingImages ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    boxShadow: uploadingImages ? 'none' : '0 0 20px rgba(0, 240, 255, 0.3)',
                    opacity: uploadingImages ? 0.5 : 1
                  }}
                >
                  <Camera style={{ width: 18, height: 18 }} />
                  {uploadingImages ? 'Processing...' : 'Camera'}
                </button>

                <button
                  type="button"
                  onClick={handleFileUpload}
                  disabled={uploadingImages}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'linear-gradient(135deg, #9D4EDD, #7B2CBF)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '14px',
                    cursor: uploadingImages ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    boxShadow: uploadingImages ? 'none' : '0 0 20px rgba(157, 78, 221, 0.3)',
                    opacity: uploadingImages ? 0.5 : 1
                  }}
                >
                  <Upload style={{ width: 18, height: 18 }} />
                  {uploadingImages ? 'Processing...' : 'Upload'}
                </button>
              </div>

              {/* Image Previews */}
              {uploadedImages.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                  gap: '12px',
                  padding: '16px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '8px',
                  border: '1px solid rgba(157, 78, 221, 0.3)'
                }}>
                  {uploadedImages.map((image, index) => (
                    <div
                      key={index}
                      style={{
                        position: 'relative',
                        aspectRatio: '1',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: '2px solid rgba(157, 78, 221, 0.5)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                      }}
                    >
                      <img
                        src={image.url}
                        alt={`Return documentation ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />

                      {/* Image Type Selector */}
                      <select
                        value={image.type}
                        onChange={(e) => changeImageType(index, e.target.value)}
                        style={{
                          position: 'absolute',
                          bottom: '4px',
                          left: '4px',
                          right: '4px',
                          padding: '4px',
                          background: 'rgba(0, 0, 0, 0.8)',
                          border: '1px solid rgba(157, 78, 221, 0.5)',
                          borderRadius: '4px',
                          color: 'white',
                          fontSize: '11px',
                          fontFamily: 'Rajdhani, sans-serif',
                          fontWeight: 600
                        }}
                      >
                        <option value="packaging">Packaging</option>
                        <option value="damage">Damage</option>
                        <option value="condition">Condition</option>
                        <option value="label">Label</option>
                        <option value="other">Other</option>
                      </select>

                      {/* Remove Button */}
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        style={{
                          position: 'absolute',
                          top: '4px',
                          right: '4px',
                          padding: '6px',
                          background: 'rgba(255, 0, 110, 0.9)',
                          border: 'none',
                          borderRadius: '50%',
                          color: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                        }}
                      >
                        <Trash2 style={{ width: 14, height: 14 }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {uploadedImages.length === 0 && (
                <div style={{
                  padding: '32px',
                  textAlign: 'center',
                  background: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '8px',
                  border: '2px dashed rgba(157, 78, 221, 0.3)'
                }}>
                  <ImageIcon style={{ width: 48, height: 48, margin: '0 auto 12px', color: '#8A95AA', opacity: 0.5 }} />
                  <p style={{ color: '#8A95AA', fontSize: '14px', margin: 0 }}>
                    No images uploaded yet. Click Camera or Upload to add documentation photos.
                  </p>
                </div>
              )}
            </div>

            {/* Notes */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                color: '#9D4EDD',
                fontSize: '18px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '16px'
              }}>
                Notes
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                    INTERNAL NOTES
                  </label>
                  <textarea
                    name="internal_notes"
                    value={formData.internal_notes}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Internal notes (not visible to customer)"
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'Rajdhani, sans-serif',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                    CUSTOMER NOTES
                  </label>
                  <textarea
                    name="customer_notes"
                    value={formData.customer_notes}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Notes visible to customer"
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'Rajdhani, sans-serif',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div style={{
            padding: '24px',
            borderTop: '2px solid #9D4EDD',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px'
          }}>
            <button
              type="button"
              onClick={generateRMA}
              disabled={loading}
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #00F0FF, #00B8D4)',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                fontWeight: 700,
                fontSize: '14px',
                cursor: loading ? 'not-allowed' : 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                boxShadow: '0 0 20px rgba(0, 240, 255, 0.4)',
                opacity: loading ? 0.5 : 1
              }}
            >
              GENERATE RMA
            </button>

            {generatedRMA && !existingReturn && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 16px',
                background: 'rgba(0, 240, 255, 0.1)',
                border: '1px solid rgba(0, 240, 255, 0.3)',
                borderRadius: '6px'
              }}>
                <span style={{
                  color: '#00F0FF',
                  fontSize: '12px',
                  fontWeight: 600,
                  letterSpacing: '0.5px'
                }}>
                  {generatedRMA}
                </span>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                style={{
                  padding: '12px 24px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '14px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.5 : 1
                }}
              >
                CANCEL
              </button>

              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '12px 32px',
                  background: loading ? 'rgba(157, 78, 221, 0.5)' : 'linear-gradient(135deg, #9D4EDD, #7B2CBF)',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '14px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  boxShadow: loading ? 'none' : '0 0 20px rgba(157, 78, 221, 0.4)'
                }}
              >
                {loading ? 'SAVING...' : (isEditMode ? 'UPDATE RETURN' : 'CREATE RETURN')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
